import { useEffect, useMemo, useState } from 'react';
import { getAuthToken } from '../../../auth/tokenvalidation';
import { checkClarificationHasUnread } from './fetchClarificationResponds';

const UNREAD_FETCH_CONCURRENCY = 3;

const mapWithConcurrency = async (items, limit, mapper) => {
  const results = new Array(items.length);
  let nextIndex = 0;

  const workers = Array.from(
    { length: Math.min(limit, items.length) || 0 },
    async () => {
      while (nextIndex < items.length) {
        const index = nextIndex;
        nextIndex += 1;
        try {
          results[index] = {
            status: 'fulfilled',
            value: await mapper(items[index], index),
          };
        } catch (reason) {
          results[index] = { status: 'rejected', reason };
        }
      }
    }
  );

  await Promise.all(workers);
  return results;
};

const useClarificationUnreadByProcurement = ({
  procurementIds = [],
  procurementMitraByProcurementId = null,
  isEvaluatorView = false,
  enabled = true,
  concurrency = UNREAD_FETCH_CONCURRENCY,
  refreshKey = 0,
} = {}) => {
  const [unreadByProcurementId, setUnreadByProcurementId] = useState({});

  const procurementIdsKey = useMemo(
    () =>
      [...new Set((procurementIds ?? []).filter(Boolean))]
        .map(String)
        .sort()
        .join(','),
    [procurementIds]
  );

  const mitraMapKey = useMemo(() => {
    if (!procurementMitraByProcurementId) return '';
    return Object.entries(procurementMitraByProcurementId)
      .map(([procurementId, mitraId]) => `${procurementId}:${mitraId ?? ''}`)
      .sort()
      .join('|');
  }, [procurementMitraByProcurementId]);

  useEffect(() => {
    if (!enabled || !procurementIdsKey) {
      setUnreadByProcurementId({});
      return undefined;
    }

    let cancelled = false;

    const run = async () => {
      const token = getAuthToken();
      if (!token) {
        if (!cancelled) setUnreadByProcurementId({});
        return;
      }

      const ids = procurementIdsKey.split(',').filter(Boolean);
      const scopedEntries = ids
        .map(procurementId => ({
          procurementId,
          procurementMitraId:
            procurementMitraByProcurementId?.[procurementId] ??
            procurementMitraByProcurementId?.[String(procurementId)] ??
            null,
        }))
        .filter(entry => entry.procurementMitraId);

      if (!scopedEntries.length) {
        if (!cancelled) setUnreadByProcurementId({});
        return;
      }

      const settled = await mapWithConcurrency(
        scopedEntries,
        concurrency,
        async ({ procurementId, procurementMitraId }) => {
          const hasUnread = await checkClarificationHasUnread({
            token,
            procurementId,
            procurementMitraId,
            isEvaluatorView,
            includeClause: false,
            includeTypeResponse: true,
            allowUnscoped: false,
          });
          return [procurementId, hasUnread];
        }
      );

      if (cancelled) return;

      const next = {};
      settled.forEach(result => {
        if (result?.status !== 'fulfilled') return;
        const [procurementId, hasUnread] = result.value;
        next[procurementId] = hasUnread;
      });
      setUnreadByProcurementId(next);
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [
    concurrency,
    enabled,
    isEvaluatorView,
    mitraMapKey,
    procurementIdsKey,
    procurementMitraByProcurementId,
    refreshKey,
  ]);

  return { unreadByProcurementId };
};

export default useClarificationUnreadByProcurement;
